import type {
  GetMarkersGroupedByPositionProps,
  MapCoordinates,
  MapMarker,
  MapMarkerWithSiblings,
  ParseLatLngStringProps,
  TrimMarkerChildrenProps,
} from "./map.type";
import type { Nullable } from "@/types";

import omit from "lodash/omit";

import { getEnvironmentValues } from "@/config/environment";
import { isDebugModeActivated } from "@/config/environmentUtils";

import { LAT_LNG_DECIMAL_PRECISION } from "./map.constant";

const getGoogleMapsVariables = () => getEnvironmentValues().googleMaps;
export const getGoogleMapsApiKey = () => `${getGoogleMapsVariables().apiKey}`;
export const getGoogleMapsMapId = () => `${getGoogleMapsVariables().mapId}`;

/**
 * Rounds a latitude or longitude value to the specified number of decimal places.
 *
 * @param value - The coordinate value to round (can be null or undefined).
 * @param precision - Number of decimal places to retain. Defaults to LAT_LNG_DECIMAL_PRECISION.
 * @return The rounded coordinate. Returns 0 if input is null or undefined (e.g., "Null Island").
 */
export const roundLatLng = (
  value?: Nullable<number>,
  precision: number = LAT_LNG_DECIMAL_PRECISION
): number => {
  if (value == null) return 0;
  return parseFloat(value.toFixed(precision));
};

/**
 * @deprecated This method will become obsolete once the API starts returning coordinates as structured data.
 * Prefer using the API response directly when it includes parsed latitude and longitude values.
 *
 * Parses a string in the format "lat,lng" into a Marker object.
 */
export function parseLatLngString({
  coordinate,
}: ParseLatLngStringProps): MapCoordinates | undefined {
  const [lat, lng] = coordinate
    .split(",")
    .map((coord) => roundLatLng(parseFloat(coord)));

  if (
    typeof lat === "number" &&
    !isNaN(lat) &&
    typeof lng === "number" &&
    !isNaN(lng)
  ) {
    return {
      lat,
      lng,
    };
  }
  return undefined;
}

/**
 * Map module logger utility for debugging
 * Use this instead of direct console.info/warn calls to ensure consistent logging behavior
 */
export function logMap(
  component: string,
  message: string,
  ...data: unknown[]
): void {
  if (isDebugModeActivated("map")) {
    console.info(`[${component}] ${message}`, ...data);
  }
}

/**
 * Map module warning logger for important information
 */
export function warnMap(
  component: string,
  message: string,
  ...data: unknown[]
): void {
  if (isDebugModeActivated("map")) {
    console.warn(`[${component}] ${message}`, ...data);
  }
}

/**
 * Map module error logger for critical issues
 * Unlike other logging functions, errors are always logged regardless of debug mode
 * to ensure critical issues are not missed
 */
export function errorMap(
  component: string,
  message: string,
  ...data: unknown[]
): void {
  console.error(`[${component}] ${message}`, ...data);
}

export const getMarkersGroupedByPosition = ({
  markers,
  subItemsDepth,
}: GetMarkersGroupedByPositionProps): MapMarkerWithSiblings[] => {
  if (!markers?.length) return [];

  const bucket = groupMarkersByPosition(markers);

  return Object.values(bucket).map((group): MapMarkerWithSiblings => {
    const [firstMarker, ...siblings] = group;

    const firstMarkerTrimmed = trimMarkerChildren({
      marker: firstMarker,
      subItemsDepth,
    });

    return { ...firstMarkerTrimmed, siblings };
  });
};

export const groupMarkersByPosition = (markers: MapMarker[]) => {
  let bucket: Record<string, MapMarker[]> = {};

  markers.forEach((marker) => {
    const { position, children } = marker;
    const coordinate = `${position.lat},${position.lng}`;

    const childrenBucket = groupMarkersByPosition(children ?? []);

    const { [coordinate]: samePositionChildren, ...otherChildren } =
      childrenBucket;

    const newMarker = {
      ...marker,
      children: samePositionChildren ?? [],
    };

    bucket = {
      ...bucket,
      [coordinate]: [...(bucket[coordinate] ?? []), newMarker],
      ...otherChildren,
    };
  });

  return bucket;
};

export const trimMarkerChildren = ({
  marker,
  markerLevel = 0,
  subItemsDepth,
}: TrimMarkerChildrenProps): MapMarker => {
  if (typeof subItemsDepth !== "number") return marker;

  if (markerLevel >= subItemsDepth) return omit(marker, ["children"]);

  const trimmedChildren = marker.children?.map((child) =>
    trimMarkerChildren({
      marker: child,
      markerLevel: markerLevel + 1,
      subItemsDepth,
    })
  );

  return { ...marker, children: trimmedChildren };
};

export const getMapMarkerItemsCount = (marker: MapMarkerWithSiblings) => {
  const subItemsCount = getMapMarkerSubItemsCount(marker);

  return subItemsCount + 1; // sub items + self
};

const getMapMarkerSubItemsCount = (
  marker: MapMarker | MapMarkerWithSiblings
): number => {
  let count = 0;

  const children = marker.children ?? [];
  const siblings = "siblings" in marker ? marker.siblings : [];
  const items = [...children, ...siblings];

  items.forEach((item) => {
    const itemCount = 1;
    const subItemsCount = getMapMarkerSubItemsCount(item);
    count += itemCount + subItemsCount;
  });

  return count;
};
